/**
 * BlueprintPagesManager Component
 * ================================
 * Gestione pagine con drag-and-drop per il menu di navigazione
 */

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  GripVertical, Eye, EyeOff, Edit2, Trash2, Plus, 
  Save, X, Globe, Menu, ChevronDown, ChevronRight, ExternalLink
} from 'lucide-react';

// Sortable Page Row Component
const SortablePageRow = ({ page, onEdit, onToggleMenu, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-[#1A1A18] border border-[#2A2A28] rounded-lg mb-2 ${
        isDragging ? 'shadow-lg' : ''
      }`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-[#666] hover:text-white"
      >
        <GripVertical size={18} />
      </button>
      
      {/* Page info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium truncate">{page.title}</span>
          <span className="text-[#666] text-xs font-mono">/{page.slug}</span>
        </div>
        {page.title_it && page.title_it !== page.title && (
          <span className="text-[#888] text-sm">{page.title_it}</span>
        )}
      </div>
      
      {/* Status badges */}
      <div className="flex items-center gap-2">
        {page.show_in_top_menu && (
          <span className="px-2 py-0.5 bg-[var(--gold)]/20 text-[var(--gold)] text-xs rounded">
            Top Menu
          </span>
        )}
        {page.show_in_footer_menu && (
          <span className="px-2 py-0.5 bg-[var(--gold)]/20 text-[var(--gold)] text-xs rounded">
            Footer
          </span>
        )}
        {!page.is_published && (
          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
            Bozza
          </span>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onToggleMenu(page.id, 'show_in_top_menu')}
          className={`p-1.5 rounded ${page.show_in_top_menu ? 'text-[var(--gold)]' : 'text-[#666]'} hover:bg-[#2A2A28]`}
          title="Toggle Top Menu"
        >
          <Menu size={16} />
        </button>
        <button
          onClick={() => onEdit(page)}
          className="p-1.5 rounded text-[#888] hover:text-white hover:bg-[#2A2A28]"
          title="Modifica"
        >
          <Edit2 size={16} />
        </button>
        <button
          onClick={() => onDelete(page.id)}
          className="p-1.5 rounded text-[#888] hover:text-red-400 hover:bg-[#2A2A28]"
          title="Elimina"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

// Page Editor Modal
const PageEditorModal = ({ page, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    slug: page?.slug || '',
    title: page?.title || '',
    title_it: page?.title_it || '',
    description: page?.description || '',
    is_published: page?.is_published ?? true,
    show_in_top_menu: page?.show_in_top_menu ?? false,
    show_in_footer_menu: page?.show_in_footer_menu ?? false,
    footer_menu_column: page?.footer_menu_column || 'explore',
    requires_auth: page?.requires_auth ?? false,
  });
  const [saving, setSaving] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData, page?.id);
    setSaving(false);
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1A1A18] border border-[#2A2A28] rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#2A2A28]">
          <h3 className="text-white font-medium">
            {page ? 'Modifica Pagina' : 'Nuova Pagina'}
          </h3>
          <button onClick={onClose} className="text-[#888] hover:text-white">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Slug */}
          <div>
            <label className="block text-[#888] text-sm mb-1">Slug (URL)</label>
            <div className="flex items-center gap-2">
              <span className="text-[#666]">/</span>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                className="flex-1 bg-[#0A0A09] border border-[#2A2A28] rounded-lg px-3 py-2 text-white text-sm"
                placeholder="es. about-us"
                required
              />
            </div>
          </div>
          
          {/* Title EN */}
          <div>
            <label className="block text-[#888] text-sm mb-1">Titolo (EN)</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full bg-[#0A0A09] border border-[#2A2A28] rounded-lg px-3 py-2 text-white text-sm"
              placeholder="Page Title"
              required
            />
          </div>
          
          {/* Title IT */}
          <div>
            <label className="block text-[#888] text-sm mb-1">Titolo (IT)</label>
            <input
              type="text"
              value={formData.title_it}
              onChange={(e) => setFormData(prev => ({ ...prev, title_it: e.target.value }))}
              className="w-full bg-[#0A0A09] border border-[#2A2A28] rounded-lg px-3 py-2 text-white text-sm"
              placeholder="Titolo Pagina"
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-[#888] text-sm mb-1">Meta Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full bg-[#0A0A09] border border-[#2A2A28] rounded-lg px-3 py-2 text-white text-sm resize-none"
              rows={2}
              placeholder="Descrizione per SEO"
            />
          </div>
          
          {/* Toggles */}
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_published}
                onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                className="w-4 h-4 accent-[var(--gold)]"
              />
              <span className="text-white text-sm">Pubblicata</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requires_auth}
                onChange={(e) => setFormData(prev => ({ ...prev, requires_auth: e.target.checked }))}
                className="w-4 h-4 accent-[var(--gold)]"
              />
              <span className="text-white text-sm">Richiede Login</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.show_in_top_menu}
                onChange={(e) => setFormData(prev => ({ ...prev, show_in_top_menu: e.target.checked }))}
                className="w-4 h-4 accent-[var(--gold)]"
              />
              <span className="text-white text-sm">Mostra nel Top Menu</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.show_in_footer_menu}
                onChange={(e) => setFormData(prev => ({ ...prev, show_in_footer_menu: e.target.checked }))}
                className="w-4 h-4 accent-[var(--gold)]"
              />
              <span className="text-white text-sm">Mostra nel Footer</span>
            </label>
          </div>
          
          {/* Footer Column */}
          {formData.show_in_footer_menu && (
            <div>
              <label className="block text-[#888] text-sm mb-1">Colonna Footer</label>
              <select
                value={formData.footer_menu_column}
                onChange={(e) => setFormData(prev => ({ ...prev, footer_menu_column: e.target.value }))}
                className="w-full bg-[#0A0A09] border border-[#2A2A28] rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="explore">Explore</option>
                <option value="resources">Resources</option>
                <option value="company">Company</option>
                <option value="legal">Legal</option>
              </select>
            </div>
          )}
          
          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#2A2A28]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#888] hover:text-white text-sm"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--gold)] text-white rounded-lg text-sm hover:bg-[var(--gold-lt)] disabled:opacity-50"
            >
              {saving ? <Save size={16} className="animate-spin" /> : <Save size={16} />}
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Component
const BlueprintPagesManager = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'top-menu' | 'footer'
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Load pages
  const loadPages = useCallback(async () => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('blueprint_pages')
        .select('*')
        .order('top_menu_order', { ascending: true });
      
      if (error) throw error;
      setPages(data || []);
    } catch (err) {
      console.error('Error loading pages:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadPages();
  }, [loadPages]);
  
  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = pages.findIndex(p => p.id === active.id);
    const newIndex = pages.findIndex(p => p.id === over.id);
    
    const newPages = arrayMove(pages, oldIndex, newIndex);
    setPages(newPages);
    
    // Update order in database
    try {
      for (let i = 0; i < newPages.length; i++) {
        await supabase
          .from('blueprint_pages')
          .update({ top_menu_order: i })
          .eq('id', newPages[i].id);
      }
    } catch (err) {
      console.error('Error updating order:', err);
      loadPages(); // Reload on error
    }
  };
  
  // Toggle menu visibility
  const handleToggleMenu = async (pageId, field) => {
    const page = pages.find(p => p.id === pageId);
    if (!page) return;
    
    const newValue = !page[field];
    
    setPages(prev => prev.map(p => 
      p.id === pageId ? { ...p, [field]: newValue } : p
    ));
    
    try {
      await supabase
        .from('blueprint_pages')
        .update({ [field]: newValue })
        .eq('id', pageId);
    } catch (err) {
      console.error('Error toggling menu:', err);
      loadPages();
    }
  };
  
  // Save page
  const handleSavePage = async (formData, pageId) => {
    try {
      if (pageId) {
        // Update existing
        await supabase
          .from('blueprint_pages')
          .update(formData)
          .eq('id', pageId);
      } else {
        // Create new
        await supabase
          .from('blueprint_pages')
          .insert({
            ...formData,
            top_menu_order: pages.length,
            footer_menu_order: pages.length,
          });
      }
      
      setShowEditor(false);
      setEditingPage(null);
      loadPages();
    } catch (err) {
      console.error('Error saving page:', err);
    }
  };
  
  // Delete page
  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa pagina?')) return;
    
    try {
      await supabase
        .from('blueprint_pages')
        .delete()
        .eq('id', pageId);
      
      loadPages();
    } catch (err) {
      console.error('Error deleting page:', err);
    }
  };
  
  // Filter pages by tab
  const filteredPages = pages.filter(page => {
    if (activeTab === 'top-menu') return page.show_in_top_menu;
    if (activeTab === 'footer') return page.show_in_footer_menu;
    return true;
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[#888]">Caricamento pagine...</div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-xl font-semibold">Gestione Pagine</h2>
          <p className="text-[#888] text-sm mt-1">
            Trascina per riordinare il menu. {pages.length} pagine totali.
          </p>
        </div>
        
        <button
          onClick={() => {
            setEditingPage(null);
            setShowEditor(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--gold)] text-white rounded-lg text-sm hover:bg-[var(--gold-lt)]"
        >
          <Plus size={16} />
          Nuova Pagina
        </button>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#2A2A28] pb-2">
        {[
          { id: 'all', label: 'Tutte', count: pages.length },
          { id: 'top-menu', label: 'Top Menu', count: pages.filter(p => p.show_in_top_menu).length },
          { id: 'footer', label: 'Footer', count: pages.filter(p => p.show_in_footer_menu).length },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-t-lg text-sm transition-colors ${
              activeTab === tab.id
                ? 'bg-[#2A2A28] text-white'
                : 'text-[#888] hover:text-white'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>
      
      {/* Pages List with DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={filteredPages.map(p => p.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {filteredPages.map((page) => (
              <SortablePageRow
                key={page.id}
                page={page}
                onEdit={(p) => {
                  setEditingPage(p);
                  setShowEditor(true);
                }}
                onToggleMenu={handleToggleMenu}
                onDelete={handleDeletePage}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      
      {filteredPages.length === 0 && (
        <div className="text-center py-12 text-[#666]">
          Nessuna pagina trovata in questa categoria
        </div>
      )}
      
      {/* Preview Links */}
      <div className="mt-6 p-4 bg-[#0A0A09] border border-[#2A2A28] rounded-lg">
        <h4 className="text-[#888] text-sm mb-3">Anteprima Menu</h4>
        <div className="flex flex-wrap gap-2">
          {pages.filter(p => p.show_in_top_menu && p.is_published).map(page => (
            <a
              key={page.id}
              href={`/${page.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A18] border border-[#2A2A28] rounded text-white text-sm hover:border-[var(--gold)]"
            >
              {page.title_it || page.title}
              <ExternalLink size={12} />
            </a>
          ))}
        </div>
      </div>
      
      {/* Editor Modal */}
      {showEditor && (
        <PageEditorModal
          page={editingPage}
          onSave={handleSavePage}
          onClose={() => {
            setShowEditor(false);
            setEditingPage(null);
          }}
        />
      )}
    </div>
  );
};

export default BlueprintPagesManager;
